import React, { useEffect, useState } from "react";
import Grid from "@material-ui/core/Grid";
import { useFormik } from "formik";
import Box from "@material-ui/core/Box";
import Divider from "@material-ui/core/Divider";
import Toolbar from "@material-ui/core/Toolbar";
import { PrimaryButton, SecondaryButton } from "@prestosports/commons-ui";
import { FootballContextUtils } from "@prestosports/prestostats-core/Common";
import EventTimePicker from "./EventTimePicker";
import FormMessages from "../../errors/components/FormMessages";
import gameControlStyles from "../styles/GameControl.styles";
import GamePeriod from "./GamePeriod";
import ValidationErrors from "../../common/components/ValidationErrors";
import { useAppContext } from "../../../state/app/context";
import { useGameContextContext } from "../../../state/game-context/context";

const GameControl = ({
	seconds,
	period,
	periodsDisabled,
	onConfirm,
	onCancel,
	validationErrors
}) => {
	const classes = gameControlStyles();

	const { state: appState } = useAppContext();
	const isSubmitting = appState.isSubmitting();

	const { state: contextState } = useGameContextContext();
	const gameConfig = contextState.gameConfig();

	const [errors, setErrors] = useState({});
	const [infos, setInfos] = useState({});
	const [suggestedSeconds, setSuggestedSeconds] = useState(seconds);
	const [isTimeDisabled, setTimeDisabled] = useState(false);

	const formik = useFormik({
		initialValues: {
			seconds,
			period
		},
		onSubmit: ({ seconds: sec, period: p }) => {
			if (onConfirm) {
				onConfirm({
					seconds: sec,
					period: p
				});
			}
		}
	});

	const { dirty, handleSubmit, setFieldValue } = formik;

	const setTotalSeconds = (value = {}) => {
		setFieldValue("seconds", value);
	};

	const onChangePeriod = (value) => {
		setFieldValue("period", value);
		const periodChanged = period !== value;
		setTimeDisabled(periodChanged);
		if (periodChanged) {
			setSuggestedSeconds(
				FootballContextUtils.getPeriodStartSeconds(
					gameConfig.general.minutesByPeriod
				)
			);
		} else {
			setSuggestedSeconds(seconds);
		}
	};

	const cancel = () => {
		if (onCancel) {
			onCancel();
		}
	};

	const hasErrors = () => {
		return !!Object.keys(errors).length;
	};

	const hasInfos = () => {
		return !!Object.keys(infos).length;
	};

	useEffect(() => {
		if (periodsDisabled) {
			setInfos({
				currentlyInPlay: "Changing quarters in the middle of a play is not allowed."
			});
		}
	}, [periodsDisabled]);

	return (
		<form onSubmit={handleSubmit}>
			<Grid container direction="column">
				{hasInfos() && (
					<Box className={classes.errorMessageContainer}>
						<FormMessages messages={infos} severity="info" />
					</Box>
				)}
				{hasErrors() && (
					<Box className={classes.errorMessageContainer}>
						<FormMessages messages={errors} />
					</Box>
				)}
				<Grid item md={12}>
					<EventTimePicker
						period={period}
						seconds={suggestedSeconds}
						onChange={setTotalSeconds}
						autoFocus
						onValidate={(isValid, _errors) => {
							setErrors(_errors);
						}}
						hideErrors
						disabled={isTimeDisabled}
					/>
				</Grid>
				<Grid item md={12}>
					<GamePeriod onChange={onChangePeriod} disabled={periodsDisabled} />
				</Grid>
				<Grid item md={12}>
					<ValidationErrors errors={validationErrors} />
				</Grid>
			</Grid>
			<Divider />
			<Toolbar>
				<Grid container justifyContent="flex-end" spacing={1}>
					<Grid item>
						<SecondaryButton
							id="cancel-button"
							onClick={cancel}
							label="Cancel"
							size="medium"
						/>
					</Grid>
					<Grid item>
						<PrimaryButton
							id="apply-button"
							label="Apply"
							size="medium"
							type="submit"
							disabled={!(!hasErrors() && dirty && !isSubmitting)}
						/>
					</Grid>
				</Grid>
			</Toolbar>
		</form>
	);
};

export default GameControl;
